import { AxiosResponse } from 'axios';
import { ZodType, z } from 'zod';
import { Result } from '@/types/common';
import { ServiceError, ServerErrorOneSchema, ServerErrorTwoSchema, PaginationResponse } from '@/types/service';
import { RQMutation, RQQuery } from '@/types/query';

export async function requestHandler<D>(request: Promise<Result<ServiceError, D>>): Promise<Result<ServiceError, D>> {
  return request.catch((error) => {
    console.error(error);
    return { kind: 'Ko', error: { kind: 'NetworkingError' } };
  });
}

export function responseHandler<D>(dataSchema: ZodType<D>) {
  return (response: AxiosResponse<unknown, unknown>): Result<ServiceError, D> => {
    return [200, 201, 202, 203, 204].includes(response.status)
      ? okDataHandler(dataSchema, response)
      : koDataHandler(response);
  };
}

function okDataHandler<D>(dataSchema: ZodType<D>, response: AxiosResponse<unknown, unknown>): Result<ServiceError, D> {
  const result = dataSchema.safeParse(response.data);
  if (result.success) {
    return { kind: 'Ok', data: result.data };
  } else {
    return { kind: 'Ko', error: { kind: 'InvalidData' } };
  }
}

function koDataHandler<D>(response: AxiosResponse<unknown, unknown>): Result<ServiceError, D> {
  const resultOne = ServerErrorOneSchema.safeParse(response.data);
  if (resultOne.success) {
    return { kind: 'Ko', error: { kind: 'ServerError', message: resultOne.data.errors.detail, errorCode: null } };
  }

  const resultTwo = ServerErrorTwoSchema.safeParse(response.data);
  return resultTwo.success
    ? {
        kind: 'Ko',
        error: {
          kind: 'ServerError',
          message: resultTwo.data.message,
          errorCode: resultTwo.data.errorCode?.toString() ?? null,
          data: response.data,
        },
      }
    : { kind: 'Ko', error: { kind: 'ServerError', message: null, errorCode: null } };
}

export function toRQQuery<D>(
  result: Result<ServiceError, D> | undefined,
  fetching: boolean,
  refetching: boolean,
  refetch: () => void,
  remove: () => void,
): RQQuery<D> {
  if (result && result.kind === 'Ok') {
    return { kind: 'Loaded', data: result.data, refetching, refetch, remove };
  } else if (result && result.kind === 'Ko') {
    return { kind: 'Error', error: result.error, refetch, remove };
  } else {
    return { kind: 'Called', fetching, refetch, remove };
  }
}

export function toRQMutation<P, D>(
  result: Result<ServiceError, D> | undefined,
  isLoading: boolean,
  mutate: (p: P) => void,
): RQMutation<P, D> {
  if (result && result.kind === 'Ok') {
    return { kind: 'Completed', data: result.data, mutate };
  } else if (result && result.kind === 'Ko') {
    return { kind: 'Error', error: result.error, mutate };
  } else if (isLoading) {
    return { kind: 'Loading', mutate };
  } else {
    return { kind: 'Called', mutate };
  }
}

export const toPaginationSchema = <D>(schema: ZodType<D>): ZodType<PaginationResponse<D>> => {
  return z.object({
    data: schema.array(),
    total: z.number(),
  });
};
